import { ExampleNotePromptDialog } from '@/components/session/ExampleNotePromptDialog';
import { ExpandedSummaryEditor } from '@/components/session/ExpandedSummaryEditor';
import { RegenerateMenu } from '@/components/session/RegenerateMenu';
import { SummaryVerification } from '@/components/session/SummaryVerification';
import { TemplateQuickEditDialog } from '@/components/session/TemplateQuickEditDialog';
import { TemplateRequiredHint } from '@/components/session/TemplateRequiredHint';
import { ComposerNoteBody } from '@/components/session/note-instructions/ComposerNoteBody';
import { NoteInstructionsFooter } from '@/components/session/note-instructions/NoteInstructionsFooter';
import { NoteTemplateSelect } from '@/components/session/note-instructions/NoteTemplateSelect';
import { NoteToolbar } from '@/components/session/note-instructions/NoteToolbar';
import {
  activeCountClasses,
  highlightedTaskClasses,
  noteTabClasses,
  noteTabListClasses,
} from '@/components/session/note-instructions/noteInstructions.styles';
import { getInstructionSuggestions } from '@/components/session/note-instructions/suggestionRules';
import type { ClinicalTemplate } from '@/components/templates/template-utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { hasBetaAccess, hasSummaryVerificationAccess, hasVerkefniAccess, useAuth } from '@/contexts/AuthContext';
import type { BloodTestOrderPatient } from '@/features/blood-tests/bloodTests.types';
import { BloodTestOrderSheet } from '@/features/blood-tests/components/BloodTestOrderSheet';
import { resolveOrderPatient } from '@/features/blood-tests/resolveOrderPatient';
import { useSessionTutorial } from '@/features/session-tutorial/SessionTutorialProvider';
import { TutorialAnchor } from '@/features/session-tutorial/TutorialAnchor';
import { TutorialStepUnavailable } from '@/features/session-tutorial/TutorialStepUnavailable';
import { useExampleNotePrompt } from '@/hooks/useExampleNotePrompt';
import { useGenerateSummary } from '@/hooks/useGenerateSummary';
import type { NoteInstructions } from '@/hooks/useNoteInstructions';
import { useSettings } from '@/hooks/useSettings';
import { useSummaryVerification } from '@/hooks/useSummaryVerification';
import { useTemplateDefaults } from '@/hooks/useTemplateDefaults';
import type { Document } from '@/lib/mockData';
import { ApiError } from '@/lib/queryClient';
import { cn } from '@/lib/utils';
import type { SummaryStyle } from '@/types/summaryStyle';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  Calendar,
  Check,
  ClipboardList,
  Copy,
  Droplets,
  Eye,
  FileCheck,
  FileCode,
  FileText,
  ListTodo,
  Loader2,
  Mail,
  Maximize2,
  MessageSquareText,
  Minus,
  Pill,
  Plus,
  Save,
  Scan,
  Send,
  Sparkles,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AttributedSummary, type AttributedSummaryData } from './AttributedSummary';
import { SamhengiPanel } from './SamhengiPanel';
import { SummaryFeedback } from './SummaryFeedback';
import {
  BookingSheet,
  PatientLetterSheet,
  PrescriptionSheet,
  RequestLetterSheet,
  isRequestLetterType,
} from './task-sheets';

interface DictationTemplate {
  id: number;
  name: string;
  description: string | null;
  template_content: string;
  is_personal?: boolean;
  scope?: string;
}

interface SmartPanelProps {
  summary: string;
  documents: Document[];
  isProcessing: boolean;
  dictationMode: boolean;
  onToggleDictation: () => void;
  dictationText: string;
  onDictationChange: (text: string) => void;
  transcript?: string; // Full transcript for SOAP note generation
  onGenerateSummary?: (style?: SummaryStyle) => void; // External summary generation (for conversation mode with templates)
  isGeneratingSummary?: boolean; // Loading state for external summary generation
  isTranscriptionActive?: boolean; // Block token-consuming summary work during active capture
  summaryTemplateInfo?: { baseTemplate: string; clinicalTemplate: string | null } | null; // Template info
  summaryGenerationTemplateId?: () => number | null;
  conversationTemplateId?: number | null; // Selected template for conversation mode
  // Callback when the template changes. `options.persist` is false when the
  // panel is only clearing a selection it couldn't resolve (template missing
  // from this org's list) — the parent must NOT write that to the server.
  onConversationTemplateChange?: (templateId: number | null, options?: { persist?: boolean }) => void;
  // Summary editing props
  editedSummary?: string | null; // Current edited summary (null if not editing)
  onSummaryEdit?: (text: string) => void; // Callback when summary is edited
  onSummarySave?: () => void; // Callback to save edited summary
  isSavingSummary?: boolean; // Loading state for saving summary
  onCopySummary?: (text: string) => void; // Persist + audit copied summary text (MDR)
  // Page-owned note-instruction composer state (beta), shared by desktop and mobile panels.
  noteInstructions?: NoteInstructions;
  // Attribution props for linking summary to transcript/audio
  attributedSummary?: AttributedSummaryData | null; // Structured summary with segment attributions
  onHighlightSegments?: (segmentIds: string[] | null) => void; // Highlight transcript segments on hover
  onSeekToTime?: (time: number) => void; // Seek audio to timestamp on click
  // Session/patient info
  sessionId?: string;
  patientId?: number;
  patientName?: string;
  patient?: BloodTestOrderPatient; // Locks blood-test orders to this patient when it matches the todo's patient
  // Task state from useTasks hook (parent owns task logic)
  tasks: Document[];
  confirmedTasks: Set<string>;
  onToggleTaskCompletion: (doc: Document, completed: boolean) => void;
  onTaskContentSaved?: (doc: Document, content: string, extraData: Record<string, unknown>) => void;
  // Ephemeral pre-encounter context (PMH, meds, complaints) typed by the
  // clinician and prepended to the summary prompt. Stored client-side only.
  onContextChange?: (text: string) => void;
}

export function SmartPanel({
  summary,
  documents,
  isProcessing,
  dictationMode,
  onToggleDictation: _onToggleDictation,
  dictationText,
  onDictationChange: _onDictationChange,
  transcript,
  onGenerateSummary,
  isGeneratingSummary,
  isTranscriptionActive = false,
  summaryTemplateInfo: _summaryTemplateInfo,
  conversationTemplateId,
  summaryGenerationTemplateId,
  onConversationTemplateChange,
  editedSummary,
  onSummaryEdit: onSummaryEditProp,
  onSummarySave,
  isSavingSummary,
  onCopySummary,
  noteInstructions,
  attributedSummary,
  onHighlightSegments,
  onSeekToTime,
  sessionId,
  patientId,
  patientName,
  patient,
  tasks: detectedTasks,
  confirmedTasks,
  onToggleTaskCompletion: toggleTaskCompletion,
  onTaskContentSaved,
  onContextChange,
}: SmartPanelProps) {
  const { user, viewingAsAuthAccountId } = useAuth();
  const showVerkefni = hasVerkefniAccess(user);
  // Summary verification (live "Yfirferð") is beta-only.
  const showSummaryVerification = hasSummaryVerificationAccess(user);
  // "Ask Skríban" note-instruction composer is beta-only, needs a way to save the note, and
  // only works on the clinician's own sessions (not a delegate view).
  const instructions = hasBetaAccess(user) && sessionId && !viewingAsAuthAccountId ? noteInstructions : undefined;
  // No manual note edits while a composer write is in flight (its save would clear the draft).
  const onSummaryEdit = instructions?.isBusy ? undefined : onSummaryEditProp;
  const { summaryFontSize, setSummaryFontSize } = useSettings();
  const [activeTab, setActiveTab] = useState(dictationMode ? 'nota' : 'summary');
  // Composer-touched tasks are out of view: green Verkefni badge, and ledger labels link to the tab.
  const tasksOffscreen = showVerkefni && activeTab !== 'documents';
  const [soapNote, setSoapNote] = useState<string>('');
  // Clicking the note opens it for editing; only a real change counts as unsaved.
  const isEditingNote = editedSummary != null && editedSummary !== (summary || soapNote);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [copiedSummary, setCopiedSummary] = useState(false);
  const exampleNotePrompt = useExampleNotePrompt({ templateId: conversationTemplateId, sessionId });
  const summaryTextareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingCursorPos = useRef<number | null>(null);
  const [isExpandedEditor, setIsExpandedEditor] = useState(false);

  // Async cross-model verification of the generated summary against the transcript.
  // Runs in the background and re-runs whenever the summary text changes.
  const verifiableSummary = summary || soapNote || null;
  // The clinician asked for the composer's edits, so a note it wrote isn't re-verified.
  const isComposerNote = !!instructions?.entries.some((entry) => entry.note?.next === verifiableSummary);
  const verification = useSummaryVerification({
    sessionId: sessionId ?? null,
    summary: verifiableSummary,
    transcription: transcript ?? null,
    enabled: showSummaryVerification && !isTranscriptionActive && !isComposerNote,
  });

  // Applying a verification fix edits the summary AND switches the panel into edit
  // mode, so the corrected text is immediately visible (the default attributed view
  // renders from attribution data, not from editedSummary).
  const handleApplyVerificationFix = (text: string) => {
    onSummaryEdit?.(text);
    setIsEditingMode(true);
  };

  // Reset edit mode when save completes (editedSummary becomes null)
  // This ensures AttributedSummary is shown again after saving
  const prevEditedSummaryRef = useRef(editedSummary);
  useEffect(() => {
    // If editedSummary was truthy and is now null/undefined, a save completed
    if (prevEditedSummaryRef.current && !editedSummary) {
      setIsEditingMode(false);
    }
    prevEditedSummaryRef.current = editedSummary;
  }, [editedSummary]);

  // Keyboard shortcut: Cmd+S / Ctrl+S to save summary when in edit mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's' && isEditingMode && onSummarySave && editedSummary) {
        e.preventDefault();
        onSummarySave();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditingMode, onSummarySave, editedSummary]);

  // Set cursor position in textarea after entering edit mode from a click
  useEffect(() => {
    if (isEditingMode && summaryTextareaRef.current && pendingCursorPos.current != null) {
      const pos = pendingCursorPos.current;
      const ta = summaryTextareaRef.current;
      pendingCursorPos.current = null;
      // Use requestAnimationFrame to ensure textarea is rendered and populated
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(pos, pos);
        // Scroll textarea so cursor is visible — estimate line height to scroll near cursor
        const textBeforeCursor = ta.value.slice(0, pos);
        const linesBefore = textBeforeCursor.split('\n').length;
        const lineHeight = 20; // approximate px per line
        ta.scrollTop = Math.max(0, linesBefore * lineHeight - ta.clientHeight / 3);
      });
    }
  }, [isEditingMode]);

  const { mutateAsync: requestSummary, isPending: isGeneratingSoap } = useGenerateSummary();
  const [editingBloodTest, setEditingBloodTest] = useState<Document | null>(null);
  const [editingPrescription, setEditingPrescription] = useState<Document | null>(null);
  const [editingBooking, setEditingBooking] = useState<Document | null>(null);
  const [editingPatientLetter, setEditingPatientLetter] = useState<Document | null>(null);
  const [editingRequest, setEditingRequest] = useState<Document | null>(null);

  const getDocumentEditHandler = (doc: Document): (() => void) | undefined => {
    switch (doc.type) {
      case 'bloodtest':
        // Ordering needs the persisted todo id as the order's context.
        return doc.dbId ? () => setEditingBloodTest(doc) : undefined;
      case 'prescription':
        return () => setEditingPrescription(doc);
      case 'booking':
        return () => setEditingBooking(doc);
      case 'patient_letter':
        return () => setEditingPatientLetter(doc);
      default:
        return isRequestLetterType(doc.type) ? () => setEditingRequest(doc) : undefined;
    }
  };

  const isGeneratingRef = useRef<boolean>(false);
  const _initialSessionIdRef = useRef<string | undefined>(sessionId);

  // Dictation template state
  const { dictation: defaultDictationTemplateId, conversation: defaultConversationTemplateId } = useTemplateDefaults();
  const [availableTemplates, setAvailableTemplates] = useState<DictationTemplate[]>([]);
  // Settled (loaded or failed): until then an empty list does not mean "no templates".
  const [hasFetchedTemplates, setHasFetchedTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [templateUsedName, setTemplateUsedName] = useState<string | null>(null);
  const [isTemplateSelectOpen, setIsTemplateSelectOpen] = useState(false);
  const tutorial = useSessionTutorial();
  // The tour's template step is done once a template is picked, or the list is closed with one
  // already chosen (picking the selected one again fires no value change).
  const handleTemplateSelectOpenChange = (open: boolean) => {
    setIsTemplateSelectOpen(open);
    if (!open && conversationTemplateId != null) tutorial?.complete('template');
  };
  const selectConversationTemplate = (templateId: number) => {
    onConversationTemplateChange?.(templateId);
    tutorial?.complete('template');
  };
  const selectDictationTemplate = (templateId: number) => {
    setSelectedTemplateId(templateId);
    // Notify parent of template change (for saving summaries)
    onConversationTemplateChange?.(templateId);
    // Clear current note to regenerate with new template
    if (soapNote) {
      setSoapNote('');
    }
    tutorial?.complete('template');
  };
  // Full-template quick edit (opened from the eye icon next to the template dropdown).
  const [editTemplate, setEditTemplate] = useState<ClinicalTemplate | null>(null);
  const [isTemplateEditOpen, setIsTemplateEditOpen] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Fetch available templates once on mount; the selection sync below reacts
  // to defaults/selection changes without refetching the list.
  useEffect(() => {
    async function fetchTemplates() {
      try {
        const response = await fetch('/api/templates', { credentials: 'include' });
        if (response.ok) {
          setAvailableTemplates(await response.json());
        }
      } catch (error) {
        console.error('[SmartPanel] Error fetching templates:', error);
      } finally {
        setHasFetchedTemplates(true);
      }
    }

    fetchTemplates();
  }, []);

  // Keep the selection consistent with the fetched list and the
  // server-persisted default.
  // biome-ignore lint/correctness/useExhaustiveDependencies: onConversationTemplateChange intentionally excluded
  useEffect(() => {
    // A transient empty list must not clear the user's selection — the
    // server default is authoritative and non-destructive here.
    if (availableTemplates.length === 0) return;

    const storedTemplateId = dictationMode ? defaultDictationTemplateId : defaultConversationTemplateId;
    const currentTemplateId = conversationTemplateId ?? storedTemplateId;
    const currentTemplateExists =
      currentTemplateId === null ||
      availableTemplates.some((template: DictationTemplate) => template.id === currentTemplateId);

    if (!currentTemplateExists) {
      // The id isn't in this org's list (deleted, or set in another org).
      // Deselect locally only — persisting null here would destroy the
      // default the user set elsewhere.
      if (dictationMode) {
        setSelectedTemplateId(null);
      }
      onConversationTemplateChange?.(null, { persist: false });
      return;
    }

    if (dictationMode) {
      setSelectedTemplateId(currentTemplateId);
    }
    // The default is seeded into the parent's selection directly from
    // useTemplateDefaults, so we don't push it back up here — doing so
    // would re-persist the (unchanged) default on every session load.
  }, [
    availableTemplates,
    dictationMode,
    defaultDictationTemplateId,
    defaultConversationTemplateId,
    conversationTemplateId,
  ]);

  // Generate SOAP note from transcript
  const generateSoapNote = async (style?: SummaryStyle) => {
    if (isTranscriptionActive) return;

    const textToFormat = transcript || dictationText;
    if (!textToFormat || textToFormat.trim().length === 0) {
      return;
    }

    if (isGeneratingRef.current) {
      return;
    }

    isGeneratingRef.current = true;
    try {
      const data = await requestSummary({
        transcript: textToFormat,
        mode: dictationMode ? 'dictation' : 'conversation',
        template_id: dictationMode ? (selectedTemplateId ?? undefined) : undefined,
        style,
      });
      setSoapNote(data.soap_note);
      setTemplateUsedName(data.template_used || null);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        console.warn('[SmartPanel] Authentication required to generate summary');
        return;
      }
      console.error('[SmartPanel] Error generating SOAP note:', error);
    } finally {
      isGeneratingRef.current = false;
    }
  };

  // Generation requires a template. Both pages pass their selection as
  // conversationTemplateId in either mode, so this mirrors the page-level guard
  // exactly (the panel's internal dictation selection lags until /api/templates
  // resolves).
  const hasTemplate = conversationTemplateId != null;
  // Only offer the "open selector" shortcut when a selector is actually mounted.
  const openTemplateSelector =
    availableTemplates.length > 0 && (dictationMode || onConversationTemplateChange)
      ? () => setIsTemplateSelectOpen(true)
      : undefined;

  // Single entry point for every manual "generate note" / "regenerate" button.
  const handleGenerateSummary = (style?: SummaryStyle) => {
    if (isTranscriptionActive || !hasTemplate) return;

    if (onGenerateSummary) {
      onGenerateSummary(style);
    } else {
      void generateSoapNote(style);
    }
  };

  // Combine passed-in documents with tasks from parent. The BFF derives org scope from the session.
  const allDocuments = [...documents, ...detectedTasks];

  const currentNote = editedSummary ?? (summary || soapNote);
  // Show the diff only while the note still equals what the instruction produced.
  const activeNoteChange =
    !isEditingMode && instructions?.noteChange?.next === currentNote ? instructions.noteChange : null;
  // Reads the current note/transcript/tasks at submit time (no stale closure).
  const submitInstruction = async (instruction: string) =>
    !!instructions &&
    instructions.submit({
      instruction,
      note: currentNote,
      transcript: transcript || dictationText || null,
      tasks: allDocuments,
    });
  const dismissNoteChange = () => instructions?.dismissNoteChange();
  // Start a draft from the note, unless one is open.
  const ensureDraft = () => {
    if (onSummaryEdit && !editedSummary) {
      onSummaryEdit(summary || soapNote);
    }
  };
  const openExpandedEditor = () => {
    ensureDraft();
    setIsExpandedEditor(true);
  };

  const handleCopySummary = async () => {
    const text = editedSummary ?? (summary || soapNote);
    const exampleNoteContext = exampleNotePrompt.contextKey;

    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard failures must not create MDR copy audit evidence.
      return;
    }

    setCopiedSummary(true);
    setTimeout(() => setCopiedSummary(false), 2000);
    onCopySummary?.(text);
    if (!summaryGenerationTemplateId || summaryGenerationTemplateId() !== conversationTemplateId) return;
    exampleNotePrompt.offerAfterCopy(
      availableTemplates.find((template) => template.id === conversationTemplateId),
      text,
      exampleNoteContext
    );
  };

  // Fetch the full template and open the quick-edit modal (from the eye icon).
  const openTemplateEditor = async (templateId: number) => {
    setIsLoadingPreview(true);
    try {
      const response = await fetch(`/api/templates/${templateId}`, { credentials: 'include' });
      if (response.ok) {
        setEditTemplate((await response.json()) as ClinicalTemplate);
        setIsTemplateEditOpen(true);
      }
    } catch (error) {
      console.error('[SmartPanel] Error fetching template:', error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Keep the dropdown label (and any cached copy) in sync after a quick edit.
  const handleTemplateSaved = (updated: ClinicalTemplate) => {
    setAvailableTemplates((prev) =>
      prev.map((template) =>
        template.id === updated.id
          ? {
              ...template,
              name: updated.name,
              description: updated.description,
              template_content: updated.template_content ?? template.template_content,
            }
          : template
      )
    );
  };

  // Composer panel (beta): the note sits on the page under one toolbar; see ComposerNoteBody.
  const noteTemplateId = (dictationMode ? selectedTemplateId : conversationTemplateId) ?? null;
  const canPickTemplate = availableTemplates.length > 0 && (dictationMode || !!onConversationTemplateChange);
  const isGeneratingNote = isProcessing || isGeneratingSoap || !!isGeneratingSummary;
  const hasComposerNote = !!(summary || soapNote) && !isGeneratingNote;
  const startNoteEdit = () => {
    setIsEditingMode(true);
    ensureDraft();
  };
  // Leaving the note without changing it returns to the read view (headings, attributions).
  const endUnchangedNoteEdit = () => {
    if (!isEditingNote) setIsEditingMode(false);
  };
  const composerToolbar =
    instructions && activeTab !== 'documents' ? (
      <NoteToolbar
        template={
          canPickTemplate && (
            <TutorialAnchor step="template">
              <div className="flex min-w-0 flex-1">
                <NoteTemplateSelect
                  templates={availableTemplates}
                  value={noteTemplateId}
                  open={isTemplateSelectOpen}
                  onOpenChange={handleTemplateSelectOpenChange}
                  onSelect={dictationMode ? selectDictationTemplate : selectConversationTemplate}
                />
              </div>
            </TutorialAnchor>
          )
        }
        review={
          hasComposerNote &&
          showSummaryVerification && (
            <SummaryVerification
              compact
              state={verification.state}
              result={verification.result}
              summary={currentNote}
              audioId={sessionId ?? null}
              onApply={handleApplyVerificationFix}
            />
          )
        }
        menu={
          hasComposerNote || noteTemplateId
            ? {
                onRegenerate:
                  hasComposerNote && (dictationMode || onGenerateSummary || transcript)
                    ? handleGenerateSummary
                    : undefined,
                regenerateDisabled: !!isGeneratingSummary || isGeneratingSoap || isTranscriptionActive || !hasTemplate,
                onExpand: hasComposerNote ? openExpandedEditor : undefined,
                onEditTemplate: noteTemplateId ? () => void openTemplateEditor(noteTemplateId) : undefined,
                isLoadingTemplate: isLoadingPreview,
              }
            : undefined
        }
        actions={
          hasComposerNote
            ? {
                onSave: isEditingNote ? onSummarySave : undefined,
                isSaving: isSavingSummary,
                copied: copiedSummary,
                onCopy: handleCopySummary,
              }
            : undefined
        }
      />
    ) : null;
  // Shared by the panel and the large editor, so both read and edit the note the same way.
  const composerNoteBodyProps = {
    text: currentNote,
    onDismissChange: dismissNoteChange,
    attribution:
      // Attributions describe the saved note; an unsaved draft is shown as plain text.
      attributedSummary?.has_attributions && onHighlightSegments && onSeekToTime && !isEditingNote
        ? { data: attributedSummary, onHighlightSegments, onSeekToTime }
        : null,
    isEditing: isEditingMode,
    onEdit: onSummaryEdit,
    onStartEdit: startNoteEdit,
    onEditBlur: endUnchangedNoteEdit,
  };
  const composerNote =
    instructions && (summary || soapNote) ? (
      <TutorialAnchor step="result">
        <div data-testid="composer-note-view" className="min-w-0 px-2 pt-1 [overflow-wrap:anywhere]">
          {!hasTemplate && (
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3">
              <TemplateRequiredHint
                verb="endurgera"
                noun={!dictationMode && onGenerateSummary ? 'samantekt' : 'nótu'}
                onOpenSelector={openTemplateSelector}
              />
            </div>
          )}
          <ComposerNoteBody {...composerNoteBodyProps} change={activeNoteChange} />
          {sessionId && noteTemplateId && (
            <SummaryFeedback
              sessionId={sessionId}
              templateId={noteTemplateId}
              summary={currentNote}
              transcription={dictationMode ? transcript || dictationText : transcript}
            />
          )}
        </div>
      </TutorialAnchor>
    ) : null;

  // Auto-switch to nota tab if in dictation mode, summary otherwise
  useEffect(() => {
    if (dictationMode) {
      setActiveTab('nota');
    } else {
      setActiveTab('summary');
    }
  }, [dictationMode]);

  return (
    <div
      data-testid="smart-panel"
      className={cn(
        'flex h-full min-h-0 min-w-0 flex-col overflow-hidden border-border border-l',
        // Beta: outline buttons get the slate-200 border instead of the text colour.
        instructions ? 'bg-white [--button-outline:hsl(var(--border))]' : 'bg-slate-50'
      )}
    >
      <div
        className={cn(
          'shrink-0',
          instructions
            ? // The template toolbar continues this band and draws its bottom line; Verkefni has none.
              cn('h-12 bg-slate-50 px-4 pt-3', activeTab === 'documents' && 'border-border border-b')
            : 'border-border border-b bg-white p-4'
        )}
      >
        {/* With the composer, context is added by asking Skríban instead. */}
        {!instructions && <SamhengiPanel sessionId={sessionId} onChange={onContextChange} />}

        {/* Without a template no note can be made: the tour leaves out making and reading it. */}
        {!hasTemplate && (
          <>
            <TutorialStepUnavailable step="summary" />
            <TutorialStepUnavailable step="result" />
          </>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList
            className={
              instructions
                ? noteTabListClasses
                : cn('grid w-full bg-slate-100/50', showVerkefni ? 'grid-cols-2' : 'grid-cols-1')
            }
          >
            {!dictationMode && (
              <TabsTrigger value="summary" className={cn('gap-2', instructions && noteTabClasses)}>
                <Activity className="h-3.5 w-3.5" />
                Samantekt
              </TabsTrigger>
            )}
            {dictationMode && (
              <TabsTrigger value="nota" className={cn('gap-2', instructions && noteTabClasses)}>
                <FileText className="h-3.5 w-3.5" />
                Nóta
              </TabsTrigger>
            )}
            {showVerkefni && (
              <TabsTrigger value="documents" className={cn('relative gap-2', instructions && noteTabClasses)}>
                <ClipboardList className="h-3.5 w-3.5" />
                Verkefni
                {allDocuments.length > 0 && (
                  <Badge
                    variant="secondary"
                    className={cn(
                      'ml-1 h-5 px-1.5 text-xs',
                      tasksOffscreen && !!instructions?.highlightedTaskIds.size && activeCountClasses
                    )}
                  >
                    {allDocuments.length}
                  </Badge>
                )}
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
      </div>

      {composerToolbar}

      <ScrollArea data-testid="smart-panel-scroll-area" className="min-h-0 flex-1 p-4">
        <Tabs value={activeTab} className="w-full">
          <TabsContent value="summary" className="mt-0 space-y-4">
            {/* Template selector for conversation mode */}
            {!dictationMode &&
              hasFetchedTemplates &&
              !(availableTemplates.length > 0 && onConversationTemplateChange) && (
                <TutorialStepUnavailable step="template" />
              )}
            {!instructions && !dictationMode && availableTemplates.length > 0 && onConversationTemplateChange && (
              <div className="space-y-2">
                <TutorialAnchor step="template">
                  <div className="flex items-center gap-2 rounded-lg bg-slate-100/50 p-3">
                    <FileCode className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    <Select
                      open={isTemplateSelectOpen}
                      onOpenChange={handleTemplateSelectOpenChange}
                      value={conversationTemplateId?.toString() ?? ''}
                      onValueChange={(value) => selectConversationTemplate(Number.parseInt(value))}
                    >
                      <SelectTrigger className="h-8 flex-1 bg-white text-sm">
                        <SelectValue placeholder="Veldu sniðmát..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTemplates.map((template) => (
                          <SelectItem key={template.id} value={template.id.toString()}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {conversationTemplateId && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 flex-shrink-0"
                        disabled={isLoadingPreview}
                        title="Skoða og breyta sniðmáti"
                        onClick={() => openTemplateEditor(conversationTemplateId)}
                      >
                        {isLoadingPreview ? <Sparkles className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    )}
                  </div>
                </TutorialAnchor>
              </div>
            )}

            {isProcessing || isGeneratingSoap || isGeneratingSummary ? (
              <TutorialAnchor step="generating">
                <Card className={cn('border-none shadow-sm', instructions && 'bg-transparent shadow-none')}>
                  <CardContent className="p-6">
                    <div className="flex flex-col items-center justify-center space-y-4 py-8">
                      <div className="relative">
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-blue-500" />
                      </div>
                      <div className="space-y-1 text-center">
                        <p className="font-medium text-slate-700 text-sm">
                          {isGeneratingSummary
                            ? 'Býr til samantekt...'
                            : isGeneratingSoap
                              ? 'Býr til SOAP nótu...'
                              : 'Vinnur...'}
                        </p>
                        <p className="text-slate-500 text-xs">Þetta getur tekið nokkrar sekúndur</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TutorialAnchor>
            ) : composerNote ? (
              composerNote
            ) : summary || soapNote ? (
              <TutorialAnchor step="result">
                <Card className="min-w-0 max-w-full shadow-sm">
                  <CardHeader className="pb-2">
                    <div
                      data-testid="summary-card-header"
                      className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                        <CardTitle className="truncate font-medium text-muted-foreground text-sm uppercase tracking-wider">
                          {onGenerateSummary ? 'Klínísk samantekt' : 'Drög að SOAP nótu'}
                        </CardTitle>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-muted-foreground"
                          onClick={openExpandedEditor}
                        >
                          <Maximize2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-muted-foreground"
                          onClick={() => setSummaryFontSize(Math.max(10, summaryFontSize - 2))}
                          disabled={summaryFontSize <= 10}
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-muted-foreground"
                          onClick={() => setSummaryFontSize(Math.min(24, summaryFontSize + 2))}
                          disabled={summaryFontSize >= 24}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div
                        data-testid="summary-card-actions"
                        className="flex min-w-0 flex-wrap items-center gap-1 sm:shrink-0 sm:justify-end"
                      >
                        {onSummarySave && isEditingMode && editedSummary && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={onSummarySave}
                            disabled={isSavingSummary}
                            className="h-8 gap-1 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                          >
                            {isSavingSummary ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4" />
                            )}
                            Vista
                          </Button>
                        )}
                        {(onGenerateSummary || transcript) && (
                          <RegenerateMenu
                            onSelect={handleGenerateSummary}
                            disabled={isGeneratingSummary || isTranscriptionActive || !hasTemplate}
                          />
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className={cn('h-8 gap-1', copiedSummary && 'text-green-600 hover:text-green-600')}
                          onClick={handleCopySummary}
                        >
                          {copiedSummary ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          {copiedSummary ? 'Afritað' : 'Afrita'}
                        </Button>
                        {showSummaryVerification && (
                          <SummaryVerification
                            state={verification.state}
                            result={verification.result}
                            summary={editedSummary ?? (summary || soapNote)}
                            audioId={sessionId ?? null}
                            onApply={handleApplyVerificationFix}
                          />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  {!hasTemplate && (
                    <div className="mx-3 mb-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                      <TemplateRequiredHint
                        verb="endurgera"
                        noun={onGenerateSummary ? 'samantekt' : 'nótu'}
                        onOpenSelector={openTemplateSelector}
                      />
                    </div>
                  )}
                  <CardContent
                    data-testid="summary-card-content"
                    className="min-w-0 overflow-hidden p-3 [overflow-wrap:anywhere]"
                  >
                    {/* Prioritize AttributedSummary when available and not in edit mode - allows hover/click interactions */}
                    {attributedSummary?.has_attributions && onHighlightSegments && onSeekToTime && !isEditingMode ? (
                      <div
                        className="cursor-text"
                        onClick={(e) => {
                          const target = e.target as HTMLElement;
                          const isAttributedPart =
                            target.tagName === 'BUTTON' ||
                            target.getAttribute('role') === 'button' ||
                            target.closest('[role="button"]');
                          if (onSummaryEdit && !isAttributedPart) {
                            // Try to capture click position as character offset
                            const sel = window.getSelection();
                            const plainText = editedSummary ?? (summary || soapNote);
                            if (sel && sel.rangeCount > 0 && plainText) {
                              const clickedText = sel.anchorNode?.textContent ?? '';
                              const offsetInNode = sel.anchorOffset;
                              // Find this text fragment in the full summary to approximate cursor position
                              const idx = plainText.indexOf(clickedText);
                              pendingCursorPos.current = idx >= 0 ? idx + offsetInNode : null;
                            }
                            setIsEditingMode(true);
                            if (!editedSummary) {
                              onSummaryEdit(summary || soapNote);
                            }
                          }
                        }}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && onSummaryEdit) {
                            const target = e.target as HTMLElement;
                            const isAttributedPart =
                              target.tagName === 'BUTTON' ||
                              target.getAttribute('role') === 'button' ||
                              target.closest('[role="button"]');
                            if (!isAttributedPart) {
                              e.preventDefault();
                              setIsEditingMode(true);
                              if (!editedSummary) {
                                onSummaryEdit(summary || soapNote);
                              }
                            }
                          }
                        }}
                      >
                        <div style={{ fontSize: summaryFontSize }}>
                          <AttributedSummary
                            attributedSummary={attributedSummary}
                            onHighlightSegments={onHighlightSegments}
                            onSeekToTime={onSeekToTime}
                            className="whitespace-pre-wrap"
                          />
                        </div>
                      </div>
                    ) : onSummaryEdit ? (
                      <Textarea
                        ref={summaryTextareaRef}
                        value={editedSummary ?? (summary || soapNote)}
                        onChange={(e) => onSummaryEdit(e.target.value)}
                        className="min-h-[70vh] resize-none font-sans leading-relaxed"
                        style={{ fontSize: summaryFontSize }}
                        placeholder="Samantekt..."
                      />
                    ) : (
                      <div
                        className="whitespace-pre-wrap font-sans text-slate-700 leading-relaxed"
                        style={{ fontSize: summaryFontSize }}
                      >
                        {summary || soapNote}
                      </div>
                    )}
                  </CardContent>
                  {activeTab === 'summary' &&
                    sessionId &&
                    conversationTemplateId &&
                    (summary || soapNote) &&
                    !isGeneratingSummary && (
                      <SummaryFeedback
                        sessionId={sessionId}
                        templateId={conversationTemplateId}
                        summary={editedSummary ?? (summary || soapNote)}
                        transcription={transcript}
                      />
                    )}
                </Card>
              </TutorialAnchor>
            ) : (
              <div className="space-y-4 py-12 text-center">
                {transcript && !hasTemplate ? (
                  <TemplateRequiredHint
                    noun={onGenerateSummary ? 'samantekt' : 'nótu'}
                    onOpenSelector={openTemplateSelector}
                  />
                ) : (
                  <div className="text-muted-foreground text-sm">
                    {transcript
                      ? onGenerateSummary
                        ? 'Smelltu til að búa til samantekt úr samtalinu.'
                        : 'Smelltu til að búa til SOAP nótu úr samtalinu.'
                      : 'Byrjaðu upptöku til að búa til samantekt.'}
                  </div>
                )}
                {transcript && (
                  <TutorialAnchor step="summary" unavailable={!hasTemplate}>
                    <Button
                      onClick={() => handleGenerateSummary()}
                      disabled={isGeneratingSummary || isTranscriptionActive || !hasTemplate}
                      className="gap-2"
                    >
                      <Sparkles className="h-4 w-4" /> {onGenerateSummary ? 'Búa til samantekt' : 'Búa til nótu'}
                    </Button>
                  </TutorialAnchor>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="documents" className="mt-0 space-y-3">
            <AnimatePresence>
              {allDocuments.map((doc) => (
                <motion.div
                  key={doc.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  <DocumentCard
                    doc={doc}
                    onEdit={getDocumentEditHandler(doc)}
                    // No confirming while a composer write is in flight: it may still change this task.
                    onConfirm={
                      instructions?.isBusy
                        ? undefined
                        : () => {
                            const isCurrentlyConfirmed = confirmedTasks.has(doc.id) || doc.status === 'Lokið';
                            toggleTaskCompletion(doc, !isCurrentlyConfirmed);
                          }
                    }
                    isConfirmed={confirmedTasks.has(doc.id) || doc.status === 'Lokið'}
                    isHighlighted={!!instructions?.highlightedTaskIds.has(doc.id)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
            {allDocuments.length === 0 && (
              <div className="py-12 text-center text-muted-foreground text-sm">
                {transcript || dictationText
                  ? 'Engin verkefni greind ennþá. Haltu áfram að tala...'
                  : 'Engin verkefni búin til ennþá.'}
              </div>
            )}
          </TabsContent>

          <TabsContent value="nota" className="mt-0 space-y-4">
            {/* Template selector for dictation mode */}
            {dictationMode && hasFetchedTemplates && availableTemplates.length === 0 && (
              <TutorialStepUnavailable step="template" />
            )}
            {!instructions && dictationMode && availableTemplates.length > 0 && (
              <div className="space-y-2">
                <TutorialAnchor step="template">
                  <div className="flex items-center gap-2 rounded-lg bg-slate-100/50 p-3">
                    <FileCode className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    <Select
                      open={isTemplateSelectOpen}
                      onOpenChange={handleTemplateSelectOpenChange}
                      value={selectedTemplateId?.toString() ?? ''}
                      onValueChange={(value) => selectDictationTemplate(Number.parseInt(value))}
                    >
                      <SelectTrigger className="h-8 flex-1 bg-white text-sm">
                        <SelectValue placeholder="Veldu sniðmát..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTemplates.map((template) => (
                          <SelectItem key={template.id} value={template.id.toString()}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedTemplateId && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 flex-shrink-0"
                        disabled={isLoadingPreview}
                        title="Skoða og breyta sniðmáti"
                        onClick={() => openTemplateEditor(selectedTemplateId)}
                      >
                        {isLoadingPreview ? <Sparkles className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    )}
                  </div>
                </TutorialAnchor>
              </div>
            )}

            {isGeneratingSoap || isGeneratingSummary ? (
              <TutorialAnchor step="generating">
                <Card className={cn('border-none shadow-sm', instructions && 'bg-transparent shadow-none')}>
                  <CardContent className="p-6">
                    <div className="flex flex-col items-center justify-center space-y-4 py-8">
                      <div className="relative">
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-blue-500" />
                      </div>
                      <div className="space-y-1 text-center">
                        <p className="font-medium text-slate-700 text-sm">
                          {isGeneratingSummary ? 'Býr til samantekt...' : 'Býr til SOAP nótu...'}
                        </p>
                        <p className="text-slate-500 text-xs">Þetta getur tekið nokkrar sekúndur</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TutorialAnchor>
            ) : composerNote ? (
              composerNote
            ) : soapNote || summary ? (
              <TutorialAnchor step="result">
                <Card className="min-w-0 max-w-full shadow-sm">
                  <CardHeader className="pb-2">
                    <div
                      data-testid="summary-card-header"
                      className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                        <CardTitle className="truncate font-medium text-muted-foreground text-sm uppercase tracking-wider">
                          {templateUsedName ? 'Nóta' : 'SOAP Nóta'}
                        </CardTitle>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-muted-foreground"
                          onClick={openExpandedEditor}
                        >
                          <Maximize2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-muted-foreground"
                          onClick={() => setSummaryFontSize(Math.max(10, summaryFontSize - 2))}
                          disabled={summaryFontSize <= 10}
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-muted-foreground"
                          onClick={() => setSummaryFontSize(Math.min(24, summaryFontSize + 2))}
                          disabled={summaryFontSize >= 24}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div
                        data-testid="summary-card-actions"
                        className="flex min-w-0 flex-wrap items-center gap-1 sm:shrink-0 sm:justify-end"
                      >
                        {onSummarySave && editedSummary && editedSummary !== (summary || soapNote) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={onSummarySave}
                            disabled={isSavingSummary}
                            className="h-8 gap-1 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                          >
                            {isSavingSummary ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4" />
                            )}
                            Vista
                          </Button>
                        )}
                        <RegenerateMenu
                          onSelect={handleGenerateSummary}
                          disabled={isGeneratingSummary || isGeneratingSoap || isTranscriptionActive || !hasTemplate}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          className={cn('h-8 gap-1', copiedSummary && 'text-green-600 hover:text-green-600')}
                          onClick={handleCopySummary}
                        >
                          {copiedSummary ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          {copiedSummary ? 'Afritað' : 'Afrita'}
                        </Button>
                        {showSummaryVerification && (
                          <SummaryVerification
                            state={verification.state}
                            result={verification.result}
                            summary={editedSummary ?? (summary || soapNote)}
                            audioId={sessionId ?? null}
                            onApply={handleApplyVerificationFix}
                          />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  {!hasTemplate && (
                    <div className="mx-3 mb-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                      <TemplateRequiredHint verb="endurgera" noun="nótu" onOpenSelector={openTemplateSelector} />
                    </div>
                  )}
                  <CardContent
                    data-testid="summary-card-content"
                    className="min-w-0 overflow-hidden p-3 [overflow-wrap:anywhere]"
                  >
                    {/* Prioritize AttributedSummary when available and not in edit mode - allows hover/click interactions */}
                    {attributedSummary?.has_attributions && onHighlightSegments && onSeekToTime && !isEditingMode ? (
                      <div style={{ fontSize: summaryFontSize }}>
                        <AttributedSummary
                          attributedSummary={attributedSummary}
                          onHighlightSegments={onHighlightSegments}
                          onSeekToTime={onSeekToTime}
                          className="whitespace-pre-wrap"
                        />
                      </div>
                    ) : onSummaryEdit ? (
                      <Textarea
                        value={editedSummary ?? (summary || soapNote)}
                        onChange={(e) => onSummaryEdit(e.target.value)}
                        className="min-h-[70vh] resize-none font-sans leading-relaxed"
                        style={{ fontSize: summaryFontSize }}
                        placeholder="Nóta..."
                      />
                    ) : (
                      <div
                        className="whitespace-pre-wrap font-sans text-slate-700 leading-relaxed"
                        style={{ fontSize: summaryFontSize }}
                      >
                        {summary || soapNote}
                      </div>
                    )}
                  </CardContent>
                  {activeTab === 'nota' &&
                    sessionId &&
                    selectedTemplateId &&
                    (soapNote || summary) &&
                    !isGeneratingSoap && (
                      <SummaryFeedback
                        sessionId={sessionId}
                        templateId={selectedTemplateId}
                        summary={editedSummary ?? (soapNote || summary)}
                        transcription={transcript || dictationText}
                      />
                    )}
                </Card>
              </TutorialAnchor>
            ) : (
              <div className="space-y-4 py-12 text-center">
                {(transcript || dictationText) && !hasTemplate ? (
                  <TemplateRequiredHint noun="nótu" onOpenSelector={openTemplateSelector} />
                ) : (
                  <div className="text-muted-foreground text-sm">
                    {transcript || dictationText
                      ? 'Smelltu til að búa til nótu úr upptökunni.'
                      : 'Byrjaðu upptöku til að búa til nótu.'}
                  </div>
                )}
                {(transcript || dictationText) && (
                  <TutorialAnchor step="summary" unavailable={!hasTemplate}>
                    <Button
                      onClick={() => handleGenerateSummary()}
                      disabled={isTranscriptionActive || !hasTemplate}
                      className="gap-2"
                    >
                      <Sparkles className="h-4 w-4" /> Búa til nótu
                    </Button>
                  </TutorialAnchor>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </ScrollArea>

      {instructions && (
        <NoteInstructionsFooter
          instructions={instructions}
          suggestions={getInstructionSuggestions({ note: currentNote, tasks: allDocuments })}
          // Also locked during a manual edit so an instruction can't race the clinician's typing.
          disabled={!hasTemplate || isProcessing || isGeneratingSoap || !!isGeneratingSummary || isEditingNote}
          notice={isEditingNote ? UNSAVED_EDIT_NOTICE : undefined}
          onSubmit={submitInstruction}
          onShowTasks={tasksOffscreen ? () => setActiveTab('documents') : undefined}
        />
      )}

      {/* Task Editing Sheets - shared components */}
      <BloodTestOrderSheet
        task={
          editingBloodTest?.dbId
            ? {
                todoId: editingBloodTest.dbId,
                details: editingBloodTest.details,
                extraData: editingBloodTest.extraData,
                patient: resolveOrderPatient(patient, editingBloodTest.patientDbId),
                // A todo saved without a patient still starts from the session's patient.
                prefillKennitala: editingBloodTest.patientDbId == null ? patient?.ssn : undefined,
              }
            : null
        }
        onClose={() => setEditingBloodTest(null)}
        onOrdered={() => {
          if (editingBloodTest) {
            toggleTaskCompletion(editingBloodTest, true);
          }
        }}
      />

      <PrescriptionSheet
        open={!!editingPrescription}
        onOpenChange={(open) => !open && setEditingPrescription(null)}
        initialMedicationName={editingPrescription?.title}
        initialDosageInfo={editingPrescription?.details}
        onComplete={() => {
          if (editingPrescription) {
            toggleTaskCompletion(editingPrescription, true);
          }
        }}
      />

      <BookingSheet
        open={!!editingBooking}
        onOpenChange={(open) => !open && setEditingBooking(null)}
        suggestedDate={editingBooking?.details?.match(/\d{4}-\d{2}-\d{2}/)?.[0]}
        suggestedTime={editingBooking?.details?.match(/\d{2}:\d{2}/)?.[0]}
        onComplete={() => {
          if (editingBooking) {
            toggleTaskCompletion(editingBooking, true);
          }
        }}
      />

      <PatientLetterSheet
        open={!!editingPatientLetter}
        onOpenChange={(open) => !open && setEditingPatientLetter(null)}
        transcript={transcript || dictationText}
        sessionId={sessionId}
        patientId={patientId}
        patientName={patientName}
        initialContent={editingPatientLetter?.details}
        todoId={editingPatientLetter?.dbId}
        onContentSaved={(content) => {
          if (editingPatientLetter) {
            setEditingPatientLetter((prev) => (prev ? { ...prev, details: content } : prev));
          }
        }}
        onComplete={() => {
          if (editingPatientLetter) {
            toggleTaskCompletion(editingPatientLetter, true);
          }
        }}
      />

      <RequestLetterSheet
        open={!!editingRequest}
        onOpenChange={(open) => !open && setEditingRequest(null)}
        requestType={editingRequest && isRequestLetterType(editingRequest.type) ? editingRequest.type : 'referral'}
        initialTitle={editingRequest?.title}
        initialDetails={editingRequest?.details}
        initialExtraData={editingRequest?.extraData}
        transcript={transcript || dictationText}
        sessionId={sessionId}
        patientId={patientId}
        patientName={patientName}
        todoId={editingRequest?.dbId}
        onContentSaved={(content, extraData) => {
          if (editingRequest) {
            onTaskContentSaved?.(editingRequest, content, extraData);
            setEditingRequest((prev) => (prev ? { ...prev, details: content, extraData } : prev));
          }
        }}
        onComplete={() => {
          if (editingRequest) {
            toggleTaskCompletion(editingRequest, true);
          }
        }}
      />

      <ExpandedSummaryEditor
        open={isExpandedEditor}
        onOpenChange={(open) => {
          setIsExpandedEditor(open);
          // Escape unmounts the focused note textarea without a blur; leave unchanged edit mode here too.
          if (!open) endUnchangedNoteEdit();
        }}
        editedSummary={editedSummary}
        fallbackText={summary || soapNote}
        onEdit={onSummaryEdit}
        onSave={onSummarySave}
        isSaving={isSavingSummary}
        fontSize={summaryFontSize}
        title={activeTab === 'summary' ? 'Klínísk samantekt' : 'Nóta'}
      >
        {instructions && <ComposerNoteBody {...composerNoteBodyProps} change={null} />}
      </ExpandedSummaryEditor>

      <TemplateQuickEditDialog
        template={editTemplate}
        open={isTemplateEditOpen}
        onOpenChange={setIsTemplateEditOpen}
        onSaved={handleTemplateSaved}
      />

      <ExampleNotePromptDialog
        key={exampleNotePrompt.prompt?.key ?? 'closed'}
        prompt={exampleNotePrompt.prompt}
        isSaving={exampleNotePrompt.isSaving}
        onStartReview={exampleNotePrompt.startReview}
        onSave={exampleNotePrompt.saveAsExample}
        onClose={exampleNotePrompt.close}
      />
    </div>
  );
}

const UNSAVED_EDIT_NOTICE = 'Vistaðu breytingarnar á nótunni til að nota Skríbuna.';

const LETTER_TYPES = new Set(['patient_letter', 'referral', 'consultant_request', 'radiology_request']);
const LETTER_TRUNCATE_LENGTH = 120;

function DocumentCard({
  doc,
  onEdit,
  onConfirm,
  isConfirmed,
  isHighlighted,
}: {
  doc: Document;
  onEdit?: () => void;
  onConfirm?: () => void;
  isConfirmed?: boolean;
  isHighlighted?: boolean; // Temporarily marks a task created/updated by the note-instruction composer
}) {
  const [showFullLetter, setShowFullLetter] = useState(false);
  const isLongLetter = LETTER_TYPES.has(doc.type) && doc.details.length > LETTER_TRUNCATE_LENGTH;
  const detailsClass = cn('whitespace-pre-line text-xs', isConfirmed ? 'text-green-700' : 'text-muted-foreground');
  const getIcon = () => {
    switch (doc.type) {
      case 'prescription':
        return Pill;
      case 'booking':
        return Calendar;
      case 'certificate':
        return FileCheck;
      case 'bloodtest':
        return Droplets;
      case 'referral':
        return Send;
      case 'patient_letter':
        return Mail;
      case 'consultant_request':
        return MessageSquareText;
      case 'radiology_request':
        return Scan;
      case 'general_task':
        return ListTodo;
      default:
        return FileText;
    }
  };

  const Icon = getIcon();

  return (
    <Card
      className={cn(
        'group overflow-hidden border transition-colors',
        isConfirmed ? 'border-green-300' : 'border-slate-200 hover:border-primary/30',
        isHighlighted && highlightedTaskClasses
      )}
    >
      <div className="flex gap-3 p-4">
        <div
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
            isConfirmed
              ? 'bg-green-100 text-green-600'
              : 'bg-slate-100 text-slate-500 group-hover:bg-primary/10 group-hover:text-primary'
          )}
        >
          {isConfirmed ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
        </div>
        <div className="flex-1">
          <div className="mb-1 flex items-center justify-between">
            <h4 className={cn('font-medium text-sm', isConfirmed ? 'text-green-800' : 'text-slate-900')}>
              {doc.title}
            </h4>
            <Badge
              variant="outline"
              className={cn('text-[10px] uppercase', isConfirmed && 'border-green-300 bg-green-100 text-green-700')}
            >
              {isConfirmed ? 'Lokið' : doc.status}
            </Badge>
          </div>
          {isLongLetter && !showFullLetter ? (
            <div className={detailsClass}>
              <span>{doc.details.slice(0, LETTER_TRUNCATE_LENGTH)}…</span>
              <button
                type="button"
                className="ml-1 text-primary hover:underline"
                onClick={() => setShowFullLetter(true)}
              >
                Sýna bréf
              </button>
            </div>
          ) : (
            <p className={detailsClass}>
              {doc.details}
              {isLongLetter && (
                <button
                  type="button"
                  className="ml-1 text-primary hover:underline"
                  onClick={() => setShowFullLetter(false)}
                >
                  Fela
                </button>
              )}
            </p>
          )}
          <div className="mb-3" />
          <div className="flex justify-end gap-2">
            {!isConfirmed && (
              <>
                {/* Types without an edit sheet (e.g. certificate, general_task) get no dead Breyta button */}
                {onEdit && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onEdit}>
                    Breyta
                  </Button>
                )}
                <Button
                  size="sm"
                  className="h-7 bg-green-600 text-white text-xs hover:bg-green-700"
                  onClick={onConfirm}
                  disabled={doc.hasReferralAttempt || !onConfirm}
                  title={doc.hasReferralAttempt ? 'Staða tilvísunar er stýrð af sendingu.' : undefined}
                >
                  <Check className="mr-1 h-3 w-3" />
                  Staðfesta
                </Button>
              </>
            )}
            {isConfirmed && (
              <span className="flex items-center gap-1 text-green-600 text-xs">
                <Check className="h-3 w-3" /> Verkefni lokið
              </span>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
